// services/track.js
//
// Product events for Invits Office's funnels - the website's half of the
// app's src/lib/track.js, same names (shared/analyticsEvents.js), same
// database function (track_events).
//
// ── Built not to cost the page anything ────────────────────────────────────
//
//   - track() pushes onto an array and returns: no await, no network, no
//     storage on the calling path.
//   - One request per batch: after 10 seconds, at 20 events, and when the tab
//     is hidden or closed - that last one with `keepalive`, so it survives the
//     page going away (the moment most web sessions end).
//   - Not at all inside the create form's preview iframe: that page is the
//     host looking at their own invitation, not a guest visit.
import supabase from './supabase';
import { cleanProps, isEventName, SESSION_GAP_MS } from '../shared/analyticsEvents';

const URL_BASE = process.env.REACT_APP_SUPABASE_URL;
const ANON_KEY_PUBLIC = process.env.REACT_APP_SUPABASE_ANON_KEY;
const ANON_ID_KEY = 'invits.anonId';
const SESSION_KEY = 'invits.session';
const FLUSH_DELAY_MS = 10000;
const FLUSH_AT = 20;
const MAX_QUEUED = 300;

const disabled =
  typeof window === 'undefined' || window.location.pathname.startsWith('/invitation-preview');

let queue = [];
let flushTimer = null;
let accessToken = null;
let started = false;
const onceKeys = new Set();

const randomId = () =>
  `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}${Math.random().toString(36).slice(2, 10)}`;

function storageGet(key) {
  try { return window.localStorage.getItem(key); } catch { return null; }
}
function storageSet(key, value) {
  try { window.localStorage.setItem(key, value); } catch { /* private mode */ }
}

let anonId = null;
function getAnonId() {
  if (anonId) return anonId;
  anonId = storageGet(ANON_ID_KEY);
  if (!anonId) {
    anonId = randomId();
    storageSet(ANON_ID_KEY, anonId);
  }
  return anonId;
}

/** A session survives reloads and tabs, and ends after 30 idle minutes. */
function currentSession() {
  const now = Date.now();
  let session = null;
  try { session = JSON.parse(storageGet(SESSION_KEY) || 'null'); } catch { session = null; }
  if (!session || now - session.last > SESSION_GAP_MS) session = { id: randomId(), last: now, fresh: true };
  else session = { id: session.id, last: now, fresh: false };
  storageSet(SESSION_KEY, JSON.stringify({ id: session.id, last: session.last }));
  return session;
}

function send(batch, { keepalive = false } = {}) {
  if (!URL_BASE || !ANON_KEY_PUBLIC) return Promise.resolve(false);
  return fetch(`${URL_BASE}/rest/v1/rpc/track_events`, {
    method: 'POST',
    keepalive,
    headers: {
      'Content-Type': 'application/json',
      apikey: ANON_KEY_PUBLIC,
      Authorization: `Bearer ${accessToken || ANON_KEY_PUBLIC}`,
    },
    body: JSON.stringify({
      p_anon_id: getAnonId(),
      p_platform: 'web',
      p_app_version: null,
      p_events: batch,
    }),
  }).then((r) => r.ok).catch(() => false);
}

export function flushEvents({ keepalive = false } = {}) {
  if (flushTimer) { clearTimeout(flushTimer); flushTimer = null; }
  if (!queue.length) return;
  const batch = queue.slice(0, 100);
  queue = queue.slice(batch.length);
  send(batch, { keepalive }).then((ok) => {
    if (!ok && !keepalive) queue = batch.concat(queue).slice(-MAX_QUEUED);
    if (queue.length) scheduleFlush();
  });
}

function scheduleFlush(delay = FLUSH_DELAY_MS) {
  if (flushTimer) {
    if (delay > 0) return;
    clearTimeout(flushTimer);
  }
  flushTimer = setTimeout(() => { flushTimer = null; flushEvents(); }, delay);
}

/**
 * Records one event. Never throws, never waits.
 * @param {string} name   an EV value (shared/analyticsEvents.js)
 * @param {object} [props] small, flat, non-personal
 */
export function track(name, props) {
  if (disabled) return;
  try {
    if (!isEventName(name)) return;
    const session = currentSession();
    queue.push({ n: name, p: cleanProps(props), t: new Date().toISOString(), s: session.id });
    if (queue.length > MAX_QUEUED) queue = queue.slice(-MAX_QUEUED);
    scheduleFlush(queue.length >= FLUSH_AT ? 0 : FLUSH_DELAY_MS);
  } catch {
    // Analytics must never break a page.
  }
}

/** Once per `key` for the life of the page. */
export function trackOnce(key, name, props) {
  if (onceKeys.has(key)) return;
  onceKeys.add(key);
  track(name, props);
}

/** Starts the tracker. Called once, from App. */
export function startEventTracking() {
  if (disabled || started) return;
  started = true;

  supabase.auth.getSession().then(({ data }) => { accessToken = data?.session?.access_token ?? null; });
  supabase.auth.onAuthStateChange((_event, session) => { accessToken = session?.access_token ?? null; });

  track('app_opened', { path: window.location.pathname.split('/')[1] || 'home' });

  const flushNow = () => flushEvents({ keepalive: true });
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flushNow();
  });
  window.addEventListener('pagehide', flushNow);
}
